<template>
  <form
    ref="formBankDetails"
    @submit.prevent="$emit('submit', values)"
  >
    <div class="flex eu-mb_5">
      <b>
        <emd-text size="medium">{{ $t('title') }}</emd-text>
      </b>
      <emd-text
        color="#8B8B92"
        class="eu-mt_2"
        size="medium"
      >
        {{ $t('description') }}
      </emd-text>
    </div>
    <div class="flex">
      <div class="flex_group w100">
        <div class="w50">
          <label
            class="emd-label _required"
            for="bank"
            >{{ $t('bank') }}</label
          >
          <emd-select
            class="w100"
            v-model="values.bank"
            name="bank"
          >
            <option
              disabled
              selected
            >
              {{ $t('bankDefaultSelect') }}
            </option>
            <option
              v-for="[code, bank] in banks"
              :key="code"
              :value="code"
            >
              {{ bank }}
            </option>
          </emd-select>
        </div>

        <div class="w50">
          <label
            class="emd-label _required"
            for="type"
            >{{ $t('type') }}
          </label>
          <emd-select
            class="w100"
            v-model="values.type"
            name="type"
          >
            <option
              disabled
              selected
            >
              {{ $t('typeDefaultSelect') }}
            </option>
            <option
              v-for="accountType in accountTypes"
              :key="accountType"
              :value="accountType"
            >
              {{ $t(`recipients.create.type_${accountType}`) }}
            </option>
          </emd-select>
        </div>
      </div>

      <div class="flex_group w100">
        <div class="w70">
          <label
            class="emd-label _required"
            for="branchNumber"
            >{{ $t('branchNumber') }}</label
          >
          <emd-input
            class="w100"
            v-model="values.branchNumber"
            name="branchNumber"
            type="number"
            @input="$v.values.branchNumber.$touch"
            :class="{
              _state_error:
                $v.values.branchNumber.$invalid && $v.values.branchNumber.$dirty
            }"
          />
        </div>
        <div class="w30">
          <label
            class="emd-label"
            for="branchCheckDigit"
            >{{ $t('branchCheckDigit') }}</label
          >
          <emd-input
            class="w100"
            v-model="values.branchCheckDigit"
            name="branchCheckDigit"
            type="number"
            @input="$v.values.branchCheckDigit.$touch"
            :class="{
              _state_error:
                $v.values.branchCheckDigit.$invalid &&
                $v.values.branchCheckDigit.$dirty
            }"
          />
        </div>
        <div class="w70">
          <label
            class="emd-label _required"
            for="accountNumber"
            >{{ $t('accountNumber') }}</label
          >
          <emd-input
            class="w100"
            v-model="values.accountNumber"
            name="accountNumber"
            type="number"
            @input="$v.values.accountNumber.$touch"
            :class="{
              _state_error:
                $v.values.accountNumber.$invalid &&
                $v.values.accountNumber.$dirty
            }"
          />
        </div>

        <div class="w30">
          <label
            class="emd-label _required"
            for="accountCheckDigit"
            >{{ $t('accountCheckDigit') }}</label
          >
          <emd-input
            class="w100"
            v-model="values.accountCheckDigit"
            name="accountCheckDigit"
            type="number"
            @input="$v.values.accountCheckDigit.$touch"
            :class="{
              _state_error:
                $v.values.accountCheckDigit.$invalid &&
                $v.values.accountCheckDigit.$dirty
            }"
          />
        </div>
      </div>

      <div class="flex_group w100">
        <div class="w100">
          <label
            class="emd-label _required"
            for="holderDocument"
            >{{ $t('holderDocument') }}</label
          >
          <emd-input
            class="w100"
            v-model="values.holderDocument"
            name="holderDocument"
            :mask="['###.###.###-##', '##.###.###/####-##']"
            :placeholder="$t('cpfPlaceHolder')"
            @input="$v.values.holderDocument.$touch"
            :class="{
              _state_error:
                $v.values.holderDocument.$invalid &&
                $v.values.holderDocument.$dirty
            }"
          />
        </div>
      </div>

      <div class="flex_group w100">
        <div class="w100">
          <label
            class="emd-label _required"
            for="holderName"
            >{{ $t('holderName') }}</label
          >
          <emd-input
            v-model="values.holderName"
            name="holderName"
            @input="$v.values.holderName.$touch"
            :class="{
              _state_error:
                $v.values.holderName.$invalid && $v.values.holderName.$dirty
            }"
          />
          <span
            class="emd-text _size_small _color_danger eu-mt_1"
            v-if="$v.values.holderName.$error && $v.values.holderName.$dirty"
            >{{
              $t('validations.holderName', {
                min: $v.values.holderName?.$params?.minLength.min,
                max: $v.values.holderName?.$params?.maxLength.max
              })
            }}</span
          >
        </div>
      </div>
      <slot />
    </div>
  </form>
</template>
<script>
import {
  required,
  numeric,
  minLength,
  maxLength
} from 'vuelidate/lib/validators'
import { EmdInput, EmdSelect, EmdText } from 'emerald-vue/src/components'
import { CHARGES_ACCOUNT_TYPE_ENUM } from '@/enums/charges-account-type.enum'
import { isCpfValid, isCnpjValid } from '@/utils/formatCpfCnpj.utils'
import banks from '@/utils/banks.utils'

export default {
  name: 'BankDetailsForm',
  components: {
    EmdInput,
    EmdSelect,
    EmdText
  },
  props: {
    initialValues: Object
  },
  data () {
    return {
      values: {}
    }
  },
  validations () {
    return {
      values: {
        holderName: {
          required,
          minLength: minLength(5),
          maxLength: maxLength(30)
        },
        holderDocument: {
          required,
          numeric,
          isValidHolderDocument: holderDocument =>
            this.isCpfValid(holderDocument) || this.isCnpjValid(holderDocument)
        },
        bank: { required },
        branchNumber: { numeric, required },
        branchCheckDigit: {
          numeric
        },
        accountNumber: { required, numeric },
        accountCheckDigit: {
          required,
          numeric
        },
        type: { required }
      }
    }
  },
  watch: {
    '$v.$invalid': {
      handler (value) {
        this.$emit('update:isInvalid', value)
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    banks () {
      return Object.entries(banks)
    },
    accountTypes () {
      return Object.values(CHARGES_ACCOUNT_TYPE_ENUM)
    }
  },
  methods: {
    isCpfValid (cpf) {
      return isCpfValid(cpf)
    },
    isCnpjValid (cnpj) {
      return isCnpjValid(cnpj)
    }
  },
  created () {
    this.values = this.initialValues
  }
}
</script>

<style scoped>
.flex {
  display: flex;
  flex-direction: column;
}
.flex .flex_group {
  display: flex;
  flex-direction: row;
  flex: 1;
  margin-bottom: 16px;
  gap: 8px;
}
.w30 {
  width: 30%;
}
.w50 {
  width: 50%;
}
.w70 {
  width: 70%;
}
.w100 {
  width: 100%;
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}
</style>

<i18n>
{
   "pt-BR": {
    "title": "Dados bancários do cliente",
    "description": "Informe os dados da conta de quem realizou o pagamento do boleto.",
    "holderName": "Nome/Razão social do cliente",
    "type": "Tipo de Conta",
    "holderDocument": "CPF/CNPJ",
    "bank": "Banco",
    "branchNumber": "Agência",
    "branchCheckDigit": "Dígito",
    "accountNumber": "Conta",
    "accountCheckDigit": "Dígito",
    "cpfPlaceHolder": "Digite somente números",
    "bankDefaultSelect": "Selecione o banco",
    "typeDefaultSelect": "Selecione o tipo de conta",
    "validations.holderName": "Mínimo de caracteres {min}, máximo de {max}"
   },
   "en-US": {
    "title": "Bank details",
    "holderName": "Holder name",
    "type": "Account type",
    "holderDocument": "Holder document",
    "bank": "Bank",
    "branchNumber": "Branch number",
    "branchCheckDigit": "Check digit",
    "accountNumber": "Account number",
    "accountCheckDigit": "Check digit",
    "cpfPlaceHolder": "Only numbers",
    "bankDefaultSelect": "Select the bank",
    "typeDefaultSelect": "Select the account type"
   }
}
</i18n>
